import React, { useState, useEffect } from "react";
import ReactECharts from "echarts-for-react";
import { COUNTRY_OPTIONS } from "../../utils/constants/countryOptions";
import { TOOLTIP_STYLE } from "../../utils/constants/graphConfigs";
import "./miscStyle.css";
import ErrorLoading from "../404/ErrorLoading";

export default function BarChartPercepciones({ type, data, language }) {
  const [options, setOptions] = useState();

  function makeSeriesMultipleCountries(data) {
    const series = [];

    data.forEach((element) => {
      series.push({
        name: COUNTRY_OPTIONS.find((e) => e.code === element.country)
          .country_text,
        type: "bar",
        itemStyle: { borderRadius: [20, 20, 0, 0] },
        barGap: 0,
        emphasis: {
          focus: "series",
        },
        data: element.data,
      });
    });

    return series;
  }

  function makeSeriesVariable(data, isHorizontal = false) {
    const series = [];
    const dataKeys = Object.keys(data.data);

    dataKeys.forEach((key) => {
      if (
        data["show_only"] === undefined ||
        data["show_only"].includes(parseInt(key))
      ) {
        series.push({
          name: data.variables[key],
          type: "bar",
          barGap: 0,
          itemStyle: { borderRadius: isHorizontal ? [0, 20, 20, 0] : [20, 20, 0, 0] },
          emphasis: {
            focus: "series",
          },
          data: data.data[key],
        });
      }
    });

    return series;
  }

  function getOptionsSingleCountryGeneral(data) {
    if (data) {
      const singleCountryData = data[0];
      const otps = {
        colors: [
          "#C3E0E0",
          "#8FB4B5",
          "#FE7D73",
          "#F1F8E1",
          "#F8B9AB",
          "#FE8980",
        ],
        xAxis: [
          {
            type: "category",
            nameLocation: "middle",
            axisTick: { show: false },
            data: singleCountryData.categories,
            axisLabel: {
              interval: 0,
              width: 100,
              overflow: "break",
              rotate: singleCountryData.categories.length > 8 ? 45 : 0,
            },
          },
        ],
        yAxis: [
          {
            type: "value",
            showMaxLabel: "false",
            name: language === "en" ? "Percentage (%)" : "Porcentaje (%)",
          },
        ],
        label: {
          show: true,
          fontSize: "14px",
          fontFamily: "Open Sans",
          formatter: function (params) {
            return params.value.toFixed(1) + "%";
          },
        },
        grid: {
          left: "15%",
          bottom: singleCountryData.categories.length > 8 ? '25%' : '14%',
        },
        tooltip: {
          show: true,
          ...TOOLTIP_STYLE,
          formatter: function (params) {
            return (
              "<span>" +
              params.name +
              "</span><br/> " +
              params.value.toFixed(1) +
              "%"
            );
          },
        },
        series: [
          {
            type: "bar",
            itemStyle: { borderRadius: [20, 20, 0, 0] },
            data: singleCountryData.data,
          },
        ],
      };
      return otps
    }
  }

  function getOptionsSingleCountryGeneralColumn(data) {
    if (data) {
      const singleCountryData = data[0];
      const otps = {
        colors: [
          "#C3E0E0",
          "#8FB4B5",
          "#FE7D73",
          "#F1F8E1",
          "#F8B9AB",
          "#FE8980",
        ],
        xAxis: [
          {
            type: "value",
            nameLocation: "middle",
            axisTick: { show: false },
            axisLabel: {
              interval: 0,
              width: 100,
              overflow: "break",
              rotate: singleCountryData.categories.length > 8 ? 45 : 0,
            },
          },
        ],
        yAxis: [
          {
            type: "category",
            showMaxLabel: "false",
            name: language === "en" ? "Percentage (%)" : "Porcentaje (%)",
            data: singleCountryData.categories,
          },
        ],
        label: {
          show: false,
        },
        grid: {
          left: "15%",
          bottom: singleCountryData.categories.length > 8 ? '25%' : '14%',
        },
        tooltip: {
          show: true,
          ...TOOLTIP_STYLE,
          formatter: function (params) {
            return (
              "<span>" +
              params.name +
              "</span><br/> " +
              params.value.toFixed(1) +
              "%"
            );
          },
        },
        series: [
          {
            type: "bar",
            itemStyle: { borderRadius: [0, 20, 20, 0] },
            data: singleCountryData.data,
          },
        ],
      };
      return otps
    }
  }

  function getOptionsMultipleCountriesGeneral(data) {
    const legend = data.map((country, i) => {
      return COUNTRY_OPTIONS.find((e) => e.code === country.country)
        .country_text;
    });
    const categories = data[0].categories;

    if (data) {
      return {
        colors: [
          "#C3E0E0",
          "#8FB4B5",
          "#FE7D73",
          "#F1F8E1",
          "#F8B9AB",
          "#FE8980",
        ],
        legend: {
          top: 20,
          data: legend,
        },
        grid: {
          top: 80,
          bottom: "14%",
        },
        tooltip: {
          trigger: "axis",
          ...TOOLTIP_STYLE,
          axisPointer: {
            type: "shadow",
          },
          formatter: (props) => {
            const divs = [];

            divs.push("<span>" + props[0].axisValue + "</span></br>");
            props.forEach((line) => {
              divs.push(
                "<div>" +
                  line.marker +
                  " " +
                  line.seriesName +
                  ": " +
                  line.data.toFixed(1) +
                  "%</div>"
              );
            });
            return divs.join("");
          },
        },
        xAxis: [
          {
            type: "category",
            nameLocation: "middle",
            axisTick: { show: false },
            data: categories,
            axisLabel: {
              width: 100,
              overflow: "break",
            },
          },
        ],
        yAxis: [
          {
            type: "value",
            showMaxLabel: "false",
            name: "Porcentage Global (%)",
          },
        ],
        series: makeSeriesMultipleCountries(data),
      };
    }
  }

  function getOptionsSingleCountryVariable(data) {
    const singleCountryVariableData = data[0];

    const variableKeys = Object.keys(singleCountryVariableData.variables);
    const legend = [];
    variableKeys.forEach((key) => {
      if (
        singleCountryVariableData["show_only"] === undefined ||
        singleCountryVariableData["show_only"].includes(parseInt(key))
      ) {
        legend.push(singleCountryVariableData.variables[key]);
      }
    });
    if (data) {
      let otps = {
        colors: [
          "#C3E0E0",
          "#8FB4B5",
          "#FE7D73",
          "#F1F8E1",
          "#F8B9AB",
          "#FE8980",
        ],
        legend: {
          top: 10,
          data: legend,
        },
        grid: {
          top: 100,
          bottom: singleCountryVariableData.categories.length > 8 ? '25%' : '14%',
        },
        tooltip: {
          ...TOOLTIP_STYLE,
          trigger: "axis",
          axisPointer: {
            type: "shadow",
          },
          formatter: (props) => {
            const divs = [];

            divs.push("<span>" + props[0].axisValue + "</span></br>");
            props.forEach((line) => {
              divs.push(
                "<div>" +
                  line.marker +
                  " " +
                  line.seriesName +
                  ": " +
                  line.data.toFixed(1) +
                  "%</div>"
              );
            });
            return divs.join("");
          },
        },
        xAxis: [
          {
            type: "category",
            nameLocation: "middle",
            axisTick: { show: false },
            data: singleCountryVariableData.categories,
            axisLabel: {
              interval: 0,
              width: 100,
              overflow: "break",
              rotate: singleCountryVariableData.categories.length > 8 ? 45 : 0,
            },
          },
        ],
        yAxis: [
          {
            type: "value",
            showMaxLabel: "false",
            name: "Porcentage Global (%)",
          },
        ],
        series: makeSeriesVariable(singleCountryVariableData),
      }
      return otps
    }
  }

  function getOptionsSingleCountryVariableColumn(data) {
    const singleCountryVariableData = data[0];

    const variableKeys = Object.keys(singleCountryVariableData.variables);
    const legend = [];
    variableKeys.forEach((key) => {
      if (
        singleCountryVariableData["show_only"] === undefined ||
        singleCountryVariableData["show_only"].includes(parseInt(key))
      ) {
        legend.push(singleCountryVariableData.variables[key]);
      }
    });
    if (data) {
      let otps = {
        colors: [
          "#C3E0E0",
          "#8FB4B5",
          "#FE7D73",
          "#F1F8E1",
          "#F8B9AB",
          "#FE8980",
        ],
        legend: {
          top: 10,
          data: legend,
        },
        grid: {
          top: 100,
          bottom: singleCountryVariableData.categories.length > 8 ? '25%' : '14%',
          left: '20%',
        },
        tooltip: {
          ...TOOLTIP_STYLE,
          trigger: "axis",
          axisPointer: {
            type: "shadow",
          },
          formatter: (props) => {
            const divs = [];

            divs.push("<span>" + props[0].axisValue + "</span></br>");
            props.forEach((line) => {
              divs.push(
                "<div>" +
                  line.marker +
                  " " +
                  line.seriesName +
                  ": " +
                  line.data.toFixed(1) +
                  "%</div>"
              );
            });
            return divs.join("");
          },
        },
        xAxis: [
          {
            type: "value",
            nameLocation: "middle",
            axisTick: { show: false },
            
            axisLabel: {
              interval: 14,
              width: 40,
              overflow: "break",

              //rotate: singleCountryVariableData.categories.length > 8 ? 45 : 0,
            },
          },
        ],
        yAxis: [
          {
            type: "category",
            showMaxLabel: "false",
            name: "Porcentage Global (%)",
            data: singleCountryVariableData.categories,
          },
        ],
        series: makeSeriesVariable(singleCountryVariableData, true),
      }
      return otps
    }
  }

  useEffect(() => {
    //console.log("type", type);
    //console.log("data", data);
    switch (type) {
      case "single-country-general":
        setOptions(getOptionsSingleCountryGeneral(data));
        break;
      case "single-country-general-column":
        setOptions(getOptionsSingleCountryGeneralColumn(data));
        break;
      case "multiple-country-general":
        setOptions(getOptionsMultipleCountriesGeneral(data));
        break;
      case "single-country-other":
        setOptions(getOptionsSingleCountryVariable(data));
        break;
      case "single-country-other-column":
        setOptions(getOptionsSingleCountryVariableColumn(data));
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, data]);

  function EchartsContent() {
    let content;
    try {
      content = (
        <ReactECharts
          option={options}
          style={{ height: "100%", width: "100%" }}
        />
      );
    } catch (error) {
      content = <ErrorLoading language={language} />;
    }
    return content;
  }

  return options && data ? (
    <div id="perception-graph" className="set-height-small">
      <EchartsContent />
    </div>
  ) : (
    <div></div>
  );
}
